import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import { fmtDate } from '../utils/date';
import { PageError, loadError } from '../components/OfflineNotice';

const TABS = ['Income Statement', 'Balance Sheet', 'Cash Flow', "Shareholders' Equity", 'Export'];

// ── CSV helpers ───────────────────────────────────────────────────────────────
function toCSV(headers, rows) {
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
}

function downloadCSV(filename, csv) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function UpgradeWall() {
  const navigate = useNavigate();
  return (
    <div className="page">
      <BackButton />
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📈</div>
        <h2 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Enterprise Feature</h2>
        <p style={{ color: 'var(--color-muted)', maxWidth: '320px', margin: '0 auto 1.5rem' }}>
          Consolidated financials are available on the Enterprise plan.
        </p>
        <button
          onClick={() => navigate('/upgrade')}
          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', border:'none', borderRadius:'12px', padding:'1.25rem 2rem', fontWeight:700, fontSize:'1rem', cursor:'pointer', marginTop:'0.5rem' }}
        >
          View Enterprise plan →
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{
        fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em',
        marginBottom: '0.4rem', paddingBottom: '0.3rem',
        borderBottom: '1px solid var(--color-border)',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, bold, color, indent }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{
        fontSize: '0.87rem',
        paddingLeft: indent ? '1rem' : 0,
        color: bold ? 'inherit' : 'var(--color-muted)',
        fontWeight: bold ? 600 : 400,
      }}>
        {label}
      </span>
      <span style={{ fontSize: '0.87rem', fontWeight: bold ? 700 : 400, color: color || 'inherit', flexShrink: 0, marginLeft: '1rem' }}>
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', background: 'var(--color-border)', margin: '0.3rem 0' }} />;
}

// ── Export tab ───────────────────────────────────────────────────────────────
function ExportTab({ cur, stmtData, company }) {
  const [busy, setBusy] = useState({ inventory: false, assets: false, financials: false });
  const d = (k, v) => setBusy(s => ({ ...s, [k]: v }));


  const exportInventory = async () => {
    d('inventory', true);
    try {
      const r = await companyAPI.listAllProducts();
      const rows = r.data.products.map(p => [
        p.business_name, p.name, p.sku || '', p.product_type || 'finished',
        p.is_service ? 'Yes' : 'No',
        Number(p.stock_qty).toFixed(2),
        Number(p.sale_price).toFixed(2),
        Number(p.avg_cost).toFixed(2),
        p.profit_margin != null ? `${p.profit_margin}%` : '',
        Number(p.total_qty_sold).toFixed(2),
        Number(p.total_revenue).toFixed(2),
        p.reorder_level || '',
        p.suppliers || '',
        cur,
      ]);
      downloadCSV(
        `inventory-${new Date().toISOString().slice(0,10)}.csv`,
        toCSV(['Business','Product','SKU','Type','Service','Stock Qty','Sale Price','Avg Cost','Margin','Total Sold','Total Revenue','Reorder Level','Suppliers','Currency'], rows)
      );
    } catch { alert('Failed to export inventory'); } finally { d('inventory', false); }
  };

  const exportAssets = async () => {
    d('assets', true);
    try {
      const r = await companyAPI.listAllAssets();
      const rows = r.data.assets.map(a => [
        a.business_name, a.name, a.category,
        Number(a.value).toFixed(2), cur,
        a.supplier || '', fmtDate(a.acquired_at), a.description || '',
      ]);
      downloadCSV(
        `assets-${new Date().toISOString().slice(0,10)}.csv`,
        toCSV(['Business','Asset Name','Category','Value','Currency','Supplier','Acquired Date','Description'], rows)
      );
    } catch { alert('Failed to export assets'); } finally { d('assets', false); }
  };

  const exportFinancials = async () => {
    d('financials', true);
    try {
      const [sfRes, consRes] = await Promise.all([companyAPI.statements(), companyAPI.financials()]);
      const IS = sfRes.data.income_statement;
      const BS = sfRes.data.balance_sheet;
      const CF = sfRes.data.cash_flow;
      const cons = consRes.data.businesses || [];

      // Sheet 1: per-business summary
      const bizRows = cons.map(b => [
        b.name,
        Number(b.revenue).toFixed(2),
        Number(b.cogs || 0).toFixed(2),
        Number(b.gross_profit || (b.revenue - b.cogs)).toFixed(2),
        Number(b.expenses).toFixed(2),
        Number(b.net_profit || (b.revenue - b.cogs - b.expenses)).toFixed(2),
        Number(b.receivables).toFixed(2),
        Number(b.stock_value).toFixed(2),
        cur,
      ]);

      // Sheet 2: consolidated summary rows
      const summaryRows = [
        ['INCOME STATEMENT','',''],
        ['Revenue',         Number(IS.revenue).toFixed(2), cur],
        ['COGS',            Number(IS.cogs).toFixed(2),    cur],
        ['Gross Profit',    Number(IS.gross_profit).toFixed(2), cur],
        ['Expenses',        Number(IS.expenses).toFixed(2), cur],
        ['Net Profit',      Number(IS.net_profit).toFixed(2), cur],
        ['','',''],
        ['BALANCE SHEET','',''],
        ['Current Assets – Cash',           Number(BS.assets.current.cash).toFixed(2), cur],
        ['Current Assets – Receivables',    Number(BS.assets.current.receivables).toFixed(2), cur],
        ['Current Assets – Stock Value',    Number(BS.assets.current.stock_value).toFixed(2), cur],
        ['Total Current Assets',            Number(BS.assets.current.total).toFixed(2), cur],
        ['Total Fixed Assets',              Number(BS.assets.fixed.total).toFixed(2), cur],
        ['TOTAL ASSETS',                    Number(BS.assets.total).toFixed(2), cur],
        ['Total Equity',                    Number(BS.equity.total).toFixed(2), cur],
        ['','',''],
        ['CASH FLOW','',''],
        ['Net Cash from Operations',        Number(CF.operating.net).toFixed(2), cur],
        ['Net Cash from Investing',         Number(CF.investing.net).toFixed(2), cur],
        ['Net Cash from Financing',         Number(CF.financing.net).toFixed(2), cur],
        ['Net Change in Cash',              Number(CF.net_change).toFixed(2), cur],
      ];

      const bizCSV  = toCSV(['Business','Revenue','COGS','Gross Profit','Expenses','Net Profit','Receivables','Stock Value','Currency'], bizRows);
      const summCSV = toCSV(['Line Item','Amount','Currency'], summaryRows);
      const date    = new Date().toISOString().slice(0,10);

      downloadCSV(`financials-by-business-${date}.csv`, bizCSV);
      setTimeout(() => downloadCSV(`financials-consolidated-${date}.csv`, summCSV), 300);
    } catch (err) { alert(err.response?.data?.error || 'Failed to export financials'); } finally { d('financials', false); }
  };

  const BTN = [
    {
      key: 'inventory',
      icon: '📦',
      title: 'Inventory Report',
      desc: 'All products across every business — stock levels, pricing, cost and sales totals.',
      file: 'inventory.csv',
      action: exportInventory,
    },
    {
      key: 'assets',
      icon: '🏗️',
      title: 'Assets Report',
      desc: 'All fixed assets across every business — category, value, supplier and acquisition date.',
      file: 'assets.csv',
      action: exportAssets,
    },
    {
      key: 'financials',
      icon: '📊',
      title: 'Financial Statements',
      desc: 'Downloads two files: a per-business P&L summary and a consolidated statement (IS, BS, Cash Flow).',
      file: '2 CSV files',
      action: exportFinancials,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)', marginBottom: '0.25rem' }}>
        Exports cover all businesses under <strong>{company?.name}</strong>. Only the company owner can export records.
      </div>

      {BTN.map(({ key, icon, title, desc, file, action }) => (
        <div key={key} className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>{icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{title}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)', marginTop: '0.15rem' }}>{desc}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: '0.2rem', fontFamily: 'monospace' }}>
              ↓ {file}
            </div>
          </div>
          <button
            onClick={action}
            disabled={busy[key]}
            className="btn-primary"
            style={{ flexShrink: 0, padding: '0.45rem 1rem', fontSize: '0.82rem' }}
          >
            {busy[key] ? '…' : 'Export'}
          </button>
        </div>
      ))}
    </div>
  );
}

export default function CompanyFinancials() {
  const { parentCompany, isEnterprise } = useAuth();
  const [data, setData]       = useState(null);
  const [tab, setTab]         = useState(TABS[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    companyAPI.statements()
      .then(r => setData(r.data))
      // Businesses in different currencies can't be consolidated — the server
      // says so rather than adding BWP to USD.
      .catch((err) => setError(!err.response ? loadError(err) : err.response?.data?.mixed_currency
        ? err.response.data.error
        : 'Failed to load financial statements'))
      .finally(() => setLoading(false));
  }, []);

  const cur = parentCompany?.currency || '';

  const fmt = (n) =>
    `${cur} ${new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;

  const numFmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const valColor = (n) => Number(n) < 0 ? 'var(--color-danger)' : Number(n) > 0 ? 'var(--color-success)' : 'inherit';


  if (!isEnterprise) return <UpgradeWall />;
  if (loading) return <div className="page"><BackButton /><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><BackButton /><PageError error={error} /></div>;

  const IS = data.income_statement;
  const BS = data.balance_sheet;
  const CF = data.cash_flow;
  const SE = data.shareholders_equity;

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Financial Statements</h1>
      <p className="text-muted" style={{ marginBottom: '1.25rem', fontSize: '0.82rem' }}>
        {parentCompany?.name} · Consolidated
      </p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', overflowX: 'auto', paddingBottom: '2px' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flexShrink: 0,
              padding: '0.45rem 0.9rem',
              borderRadius: '999px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              background: tab === t ? 'var(--color-primary)' : 'var(--color-surface)',
              color: tab === t ? '#fff' : 'var(--color-muted)',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Income Statement ─────────────────────────────────── */}
      {tab === 'Income Statement' && (
        <div className="card" style={{ padding: '1rem 1.1rem' }}>
          <Section title="Revenue">
            <Row label="Total Sales Revenue" value={fmt(IS.revenue)} />
          </Section>
          <Section title="Cost of Goods Sold">
            <Row label="Purchase Costs (COGS)" value={fmt(IS.cogs)} />
          </Section>
          <Row label="Gross Profit" value={fmt(IS.gross_profit)} bold color={valColor(IS.gross_profit)} />
          <Divider />
          <Section title="Operating Expenses">
            <Row label="Total Expenses" value={fmt(IS.expenses)} />
          </Section>
          <Row label="Net Profit / (Loss)" value={fmt(IS.net_profit)} bold color={valColor(IS.net_profit)} />

          {IS.by_business?.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-muted)',
                textTransform: 'uppercase', letterSpacing: '0.07em',
                marginBottom: '0.5rem', paddingBottom: '0.3rem',
                borderBottom: '1px solid var(--color-border)',
              }}>
                Breakdown by Business
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                      {['Business', 'Revenue', 'Gross P.', 'Expenses', 'Net P.'].map(h => (
                        <th key={h} style={{ padding: '0.35rem 0.4rem', textAlign: h === 'Business' ? 'left' : 'right',
                          color: 'var(--color-muted)', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {IS.by_business.map(b => (
                      <tr key={b.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '0.45rem 0.4rem', fontWeight: 500 }}>{b.name}</td>
                        <td style={{ padding: '0.45rem 0.4rem', textAlign: 'right' }}>{numFmt(b.revenue)}</td>
                        <td style={{ padding: '0.45rem 0.4rem', textAlign: 'right', color: valColor(b.gross_profit) }}>{numFmt(b.gross_profit)}</td>
                        <td style={{ padding: '0.45rem 0.4rem', textAlign: 'right' }}>{numFmt(b.expenses)}</td>
                        <td style={{ padding: '0.45rem 0.4rem', textAlign: 'right', color: valColor(b.net_profit), fontWeight: 600 }}>{numFmt(b.net_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Balance Sheet ─────────────────────────────────────── */}
      {tab === 'Balance Sheet' && (
        <div className="card" style={{ padding: '1rem 1.1rem' }}>
          <Section title="Assets">
            <div style={{ fontSize: '0.74rem', fontWeight: 600, color: 'var(--color-muted)', marginBottom: '0.2rem' }}>
              Current Assets
            </div>
            <Row label="Cash & Available Funds"        value={fmt(BS.assets.current.cash)}        indent color={valColor(BS.assets.current.cash)} />
            <Row label="Receivables (outstanding)"      value={fmt(BS.assets.current.receivables)} indent />
            <Row label="Stock (at cost)"                value={fmt(BS.assets.current.stock_value)} indent />
            <Row label="Total Current Assets"           value={fmt(BS.assets.current.total)}       bold />
            <Divider />
            <div style={{ fontSize: '0.74rem', fontWeight: 600, color: 'var(--color-muted)', margin: '0.5rem 0 0.2rem' }}>
              Fixed / Non-Current Assets
            </div>
            {BS.assets.fixed.items?.length === 0
              ? <p style={{ fontSize: '0.8rem', color: 'var(--color-muted)', margin: '0.25rem 0 0.5rem 1rem' }}>No assets recorded yet.</p>
              : BS.assets.fixed.items.map(a => (
                  <Row key={a.id} label={`${a.name} (${a.business_name})`} value={fmt(a.value)} indent />
                ))
            }
            <Row label="Total Fixed Assets" value={fmt(BS.assets.fixed.total)} bold />
            <Divider />
            <Row label="TOTAL ASSETS" value={fmt(BS.assets.total)} bold />
          </Section>

          <Section title="Liabilities">
            {Number(BS.liabilities.customer_credit) > 0 && (
              <Row label="Customer overpayments (owed back)" value={fmt(BS.liabilities.customer_credit)} indent />
            )}
            <Row label="Total Liabilities" value={fmt(BS.liabilities.total)} />
            <p style={{ fontSize: '0.72rem', color: 'var(--color-muted)', margin: '0.1rem 0 0' }}>
              Purchases are treated as paid in cash — supplier credit isn't tracked yet.
            </p>
          </Section>

          <Section title="Equity">
            <Row label="Owner's Capital"     value={fmt(BS.equity.owner_capital)}     indent />
            <Row label="Retained Earnings"   value={fmt(BS.equity.retained_earnings)} indent color={valColor(BS.equity.retained_earnings)} />
            <Row label="TOTAL EQUITY"        value={fmt(BS.equity.total)}             bold color={valColor(BS.equity.total)} />
          </Section>

          <div style={{ marginTop: '0.75rem', padding: '0.55rem 0.7rem', borderRadius: '8px',
            background: 'rgba(255,255,255,0.04)', fontSize: '0.73rem', color: 'var(--color-muted)', textAlign: 'center' }}>
            Assets&nbsp;({fmt(BS.assets.total)}) = Liabilities&nbsp;({fmt(BS.liabilities.total)}) + Equity&nbsp;({fmt(BS.equity.total)}){BS.balanced ? ' ✓' : ''}
          </div>
        </div>
      )}

      {/* ── Cash Flow Statement ───────────────────────────────── */}
      {tab === 'Cash Flow' && (
        <div className="card" style={{ padding: '1rem 1.1rem' }}>
          <Section title="Operating Activities">
            <Row label="Cash received from customers"        value={fmt(CF.operating.cash_from_customers)}   indent />
            <Row label="Payments to suppliers (purchases)"   value={fmt(CF.operating.payments_to_suppliers)} indent />
            <Row label="Payments for operating expenses"     value={fmt(CF.operating.payments_for_expenses)} indent />
            <Row label="Net Cash from Operations"            value={fmt(CF.operating.net)}                   bold color={valColor(CF.operating.net)} />
          </Section>
          <Section title="Investing Activities">
            <Row label="Capital asset purchases"   value={fmt(CF.investing.asset_purchases)} indent />
            <Row label="Net Cash from Investing"   value={fmt(CF.investing.net)}             bold color={valColor(CF.investing.net)} />
          </Section>
          <Section title="Financing Activities">
            <Row label="Initial capital contributed" value={fmt(CF.financing.initial_capital)}     indent />
            <Row label="Capital injections"          value={fmt(CF.financing.capital_injections)}  indent />
            <Row label="Capital withdrawals"         value={fmt(CF.financing.capital_withdrawals)} indent />
            <Row label="Net Cash from Financing"     value={fmt(CF.financing.net)}                 bold color={valColor(CF.financing.net)} />
          </Section>
          <Divider />
          <Row label="NET CHANGE IN CASH" value={fmt(CF.net_change)} bold color={valColor(CF.net_change)} />
        </div>
      )}

      {/* ── Export ────────────────────────────────────────────── */}
      {tab === 'Export' && (
        <ExportTab cur={cur} stmtData={data} company={parentCompany} />
      )}

      {/* ── Shareholders' Equity ──────────────────────────────── */}
      {tab === "Shareholders' Equity" && (
        <>
          <div className="card" style={{ padding: '1rem 1.1rem', marginBottom: '1rem' }}>
            <Section title="Equity Composition">
              <Row label="Initial Capital Contributed"   value={fmt(SE.initial_capital)}          indent />
              <Row label="Additional Contributions"      value={fmt(SE.additional_contributions)} indent />
              <Row label="Capital Withdrawals"           value={fmt(SE.withdrawals)}              indent />
              <Row label="Retained Earnings (Net Profit)" value={fmt(SE.retained_earnings)}       indent color={valColor(SE.retained_earnings)} />
              <Divider />
              <Row label="TOTAL SHAREHOLDERS' EQUITY"   value={fmt(SE.total_equity)} bold color={valColor(SE.total_equity)} />
            </Section>
          </div>

          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-muted)',
            textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
            Capital Events History
          </div>

          {(!SE.events || SE.events.length === 0) && (
            <p className="text-muted" style={{ fontSize: '0.85rem' }}>No capital events recorded.</p>
          )}

          {SE.events?.map(ev => {
            const amt = Number(ev.amount);
            return (
              <div key={ev.id} className="card" style={{ marginBottom: '0.5rem',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '0.83rem', fontWeight: 500 }}>
                    {ev.note || (amt > 0 ? 'Capital injection' : 'Capital withdrawal')}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '0.15rem' }}>
                    {fmtDate(ev.event_at)}{ev.created_by_name ? ` · ${ev.created_by_name}` : ''}
                  </div>
                </div>
                <span style={{
                  fontWeight: 700, fontSize: '0.9rem',
                  color: amt >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
                  flexShrink: 0, marginLeft: '0.75rem',
                }}>
                  {amt >= 0 ? '+' : ''}{fmt(amt)}
                </span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
